<template>
    <div class="welcome-container">
      <div class="animated-bg"></div>
      <div class="welcome-content">
        <!-- Hero Section with Glass Effect -->
        <div class="hero-section glass-card">
          <div class="hero-badge">
            <v-icon small>mdi-lightning-bolt</v-icon>
            <span>AI-Powered</span>
          </div>
          <h1 class="hero-title">
            <span class="gradient-text">Create Professional</span>
            <br />
            <span class="highlight-text">Video Presentations</span>
            <br />
            <span class="sub-highlight">in Minutes</span>
          </h1>
          <p class="hero-subtitle">
            Transform scripts, documents, and ideas into polished multi-speaker videos through simple conversation. No editing skills or expensive agencies required.
          </p>
          <div class="hero-cta">
            <v-btn
              color="primary"
              x-large
              rounded
              elevation="0"
              @click="startFirstProject"
              class="primary-cta glass-button"
            >
              <v-icon left>mdi-play-circle</v-icon>
              Create Your First Video
            </v-btn>
            <div class="cta-divider">
              <span>or</span>
            </div>
            <v-btn
              text
              large
              rounded
              class="secondary-cta"
              @click="openDemo"
            >
              <v-icon left>mdi-play-box-outline</v-icon>
              Watch Demo
            </v-btn>
          </div>
        </div>

        <!-- Floating Stats Cards -->
        <div class="stats-section">
          <h2 class="section-title gradient-text">Why Teams Choose UnStruct.AI</h2>
          <div class="stats-grid">
            <div class="stat-card glass-card" style="--delay: 0.1s">
              <div class="stat-icon">
                <v-icon color="primary" size="40">mdi-clock-fast</v-icon>
              </div>
              <div class="stat-content">
                <h4 class="stat-value">90% Faster</h4>
                <p class="stat-desc">Than traditional video production</p>
              </div>
              <div class="stat-bg"></div>
            </div>
            <div class="stat-card glass-card" style="--delay: 0.2s">
              <div class="stat-icon">
                <v-icon color="success" size="40">mdi-currency-usd</v-icon>
              </div>
              <div class="stat-content">
                <h4 class="stat-value">Save $10-50K</h4>
                <p class="stat-desc">Per video project</p>
              </div>
              <div class="stat-bg"></div>
            </div>
            <div class="stat-card glass-card" style="--delay: 0.3s">
              <div class="stat-icon">
                <v-icon color="secondary" size="40">mdi-professional-hexagon</v-icon>
              </div>
              <div class="stat-content">
                <h4 class="stat-value">Professional Quality</h4>
                <p class="stat-desc">Without the learning curve</p>
              </div>
              <div class="stat-bg"></div>
            </div>
            <div class="stat-card glass-card" style="--delay: 0.4s">
              <div class="stat-icon">
                <v-icon color="error" size="40">mdi-shield-check</v-icon>
              </div>
              <div class="stat-content">
                <h4 class="stat-value">Trusted by Startups</h4>
                <p class="stat-desc">For investor-grade content</p>
              </div>
              <div class="stat-bg"></div>
            </div>
          </div>
        </div>

        <!-- Interactive How it Works -->
        <div class="how-it-works">
          <h2 class="section-title gradient-text">Create Your First Video in 4 Simple Steps</h2>

          <div class="steps-timeline">
            <div class="timeline-line"></div>
            <div class="steps-container">
              <div class="step-item" v-for="(step, index) in steps" :key="index" :style="{'--delay': index * 0.2 + 's'}">
                <div class="step-card glass-card">
                  <div class="step-number">{{ index + 1 }}</div>
                  <div class="step-icon-wrapper">
                    <v-icon :color="step.color" size="48">{{ step.icon }}</v-icon>
                  </div>
                  <div class="step-content">
                    <h3>{{ step.title }}</h3>
                    <p>{{ step.description }}</p>
                  </div>
                  <div class="step-hover-effect"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Conversational Interface Section -->
        <div class="interface-section glass-card">
          <div class="interface-content">
            <div class="interface-text">
              <h3 class="sub-section-title gradient-text">Create Videos the Way You Think</h3>
              <p class="interface-subtitle">Through Natural Conversation</p>
              <p class="interface-desc">
                No complex timelines or editing tools. Just chat with AI to bring your vision to life.
              </p>
            </div>
            <div class="interface-visual">
              <div class="chat-preview">
                <div class="chat-bubble user">Transform my pitch deck into a video</div>
                <div class="chat-bubble ai">I'll help you create a professional video presentation...</div>
                <div class="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modern Use Cases Grid -->
        <div class="use-cases">
          <h2 class="section-title gradient-text">Perfect For Every Use Case</h2>
          <div class="use-cases-grid">
            <div
              v-for="(useCase, index) in useCases"
              :key="index"
              class="use-case-card glass-card"
              :style="{'--delay': (index % 3) * 0.1 + 's'}"
            >
              <div class="use-case-icon">
                <v-icon :color="useCase.color" size="36">{{ useCase.icon }}</v-icon>
              </div>
              <h4>{{ useCase.title }}</h4>
              <p>{{ useCase.description }}</p>
              <div class="use-case-hover"></div>
            </div>
          </div>
        </div>

        <!-- CTA Section with Animation -->
        <div class="cta-section glass-card">
          <div class="cta-content">
            <h2 class="cta-title gradient-text">Ready to Transform Your Content?</h2>
            <p class="cta-subtitle">Join teams saving $50K+ on video production</p>
            <div class="cta-buttons">
              <v-btn
                color="primary"
                x-large
                rounded
                @click="startFirstProject"
                class="main-cta glass-button"
              >
                <v-icon left>mdi-rocket-launch</v-icon>
                Start Creating Free
              </v-btn>
              <div class="cta-features">
                <div class="feature-item">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  <span>No credit card required</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  <span>Free to start</span>
                </div>
                <div class="feature-item">
                  <v-icon small color="success">mdi-check-circle</v-icon>
                  <span>Professional results</span>
                </div>
              </div>
            </div>
          </div>
          <div class="cta-decoration">
            <div class="decoration-circle"></div>
            <div class="decoration-circle"></div>
          </div>
        </div>

        <!-- Quick Actions Float -->
        <div class="quick-actions">
          <h3 class="quick-title">Quick Start</h3>
          <div class="action-buttons">
            <v-btn
              v-for="(action, index) in quickActions"
              :key="index"
              fab
              large
              :color="action.color"
              @click="action.method"
              class="action-fab"
              :style="{'--delay': index * 0.1 + 's'}"
            >
              <v-icon>{{ action.icon }}</v-icon>
            </v-btn>
          </div>
          <div class="action-labels">
            <span v-for="(action, index) in quickActions" :key="index">{{ action.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </template>

<script>
    export default {
        name: 'ModernWelcomePage',
        data() {
          return {
            steps: [
              {
                title: "Upload Your Sources",
                description: "Start with documents, scripts, or ideas. Upload files, paste text, or import YouTube videos.",
                icon: "mdi-cloud-upload",
                color: "primary"
              },
              {
                title: "Generate Your Script",
                description: "Ask questions or give prompts to transform your content into engaging multi-speaker conversations.",
                icon: "mdi-message-text",
                color: "secondary"
              },
              {
                title: "Customize Your Video",
                description: "Add visuals, edit script sections, adjust animations, pick AI voices and preview in real-time.",
                icon: "mdi-tune-variant",
                color: "success"
              },
              {
                title: "Export & Share",
                description: "Export your finished video or present live to your audience.",
                icon: "mdi-share-variant",
                color: "error"
              }
            ],
            useCases: [
              { title: "Investor Pitches", description: "Professional pitch decks that impress", icon: "mdi-presentation", color: "primary" },
              { title: "Product Demos", description: "Showcase features with engaging videos", icon: "mdi-rocket-launch", color: "secondary" },
              { title: "Training Materials", description: "Create educational content at scale", icon: "mdi-school", color: "success" },
              { title: "Marketing Content", description: "Produce videos 10x faster", icon: "mdi-bullhorn", color: "error" },
              { title: "Sales Presentations", description: "Close deals with compelling videos", icon: "mdi-handshake", color: "warning" },
              { title: "Internal Communications", description: "Engage teams with dynamic updates", icon: "mdi-account-group", color: "info" },
              { title: "Client Reports", description: "Transform data into visual stories", icon: "mdi-file-chart", color: "primary" },
              { title: "Course Content", description: "Build engaging learning experiences", icon: "mdi-video-box", color: "secondary" },
              { title: "Success Stories", description: "Turn testimonials into narratives", icon: "mdi-trophy", color: "success" }
            ],
            quickActions: [
              { icon: "mdi-file-document", label: "Upload Docs", method: () => this.openFileUploadDialog(), color: "primary" },
              { icon: "mdi-text-box", label: "Paste Text", method: () => this.openSubmitTextDialog(), color: "secondary" },
              { icon: "mdi-youtube", label: "YouTube", method: () => this.openYoutubeSubmitDialog(), color: "error" },
              { icon: "mdi-image", label: "Upload Images", method: () => this.openImageUploadDialog(), color: "success" }
            ]
          };
        },
        methods: {
            startFirstProject() {
                this.$emit('start-first-project');
            },
            openFileUploadDialog() {
                this.$emit('open-file-upload');
            },
            openSubmitTextDialog() {
                this.$emit('open-submit-text');
            },
            openYoutubeSubmitDialog() {
                this.$emit('open-youtube-submit');
            },
            openImageUploadDialog() {
                this.$emit('open-image-upload');
            },
            openDemo() {
                // Implement demo functionality
            }
        }
    };
</script>

<style scoped>
/* Base Container */
.welcome-container {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.theme--dark .welcome-container {
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
}

/* Animated Background */
.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0.1;
  z-index: 0;
}

.animated-bg::before,
.animated-bg::after {
  content: '';
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.3) 0%, transparent 70%);
  animation: float 20s infinite ease-in-out;
}

.animated-bg::before {
  top: -200px;
  left: -200px;
  animation-delay: 0s;
}

.animated-bg::after {
  bottom: -200px;
  right: -200px;
  animation-delay: -10s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33% { transform: translate(-20px, -50px) rotate(120deg); }
  66% { transform: translate(20px, -20px) rotate(240deg); }
}

/* Glass Card Effect */
.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
}

.theme--dark .glass-card {
  background: rgba(30, 41, 59, 0.95);
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* Welcome Content */
.welcome-content {
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 60px 40px;
  margin-bottom: 60px;
  position: relative;
  overflow: hidden;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
  animation: slideDown 0.6s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-title {
  font-size: clamp(36px, 6vw, 72px);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 30px;
  animation: fadeInUp 0.8s ease-out;
}

.gradient-text {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: inline-block;
}

.highlight-text {
  color: #1f2937;
  display: inline-block;
}

.theme--dark .highlight-text {
  color: #f9fafb;
}

.sub-highlight {
  color: #667eea;
  font-size: 0.9em;
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.hero-subtitle {
  font-size: 20px;
  color: #4b5563;
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.6;
  animation: fadeIn 1s ease-out 0.3s both;
}

.theme--dark .hero-subtitle {
  color: #d1d5db;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Hero CTA */
.hero-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  animation: fadeIn 1s ease-out 0.5s both;
}

.primary-cta {
  padding: 16px 40px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease !important;
}

.glass-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
}

.glass-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(102, 126, 234, 0.3) !important;
}

.glass-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.glass-button:hover::before {
  left: 100%;
}

.cta-divider {
  color: #9ca3af;
  font-size: 14px;
  font-weight: 500;
}

.secondary-cta {
  text-transform: none !important;
  font-weight: 500 !important;
}

/* Stats Section */
.stats-section {
  margin-bottom: 100px;
}

.section-title {
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  text-align: center;
  margin-bottom: 60px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 30px;
  padding: 0 20px;
}

.stat-card {
  position: relative;
  padding: 30px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: fadeInUp 0.8s ease-out var(--delay) both;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  margin-bottom: 20px;
  animation: pulse 4s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1f2937;
}

.theme--dark .stat-value {
  color: #f9fafb;
}

.stat-desc {
  color: #6b7280;
  font-size: 15px;
  margin: 0;
}

.theme--dark .stat-desc {
  color: #d1d5db;
}

.stat-bg {
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.05) 0%, transparent 70%);
  transform: rotate(45deg);
  transition: all 0.5s ease;
  opacity: 0;
}

.stat-card:hover .stat-bg {
  opacity: 1;
  transform: rotate(45deg) scale(1.2);
}

/* Steps Timeline */
.how-it-works {
  margin-bottom: 100px;
  overflow: visible; /* Changed from default to allow numbers to show */
}

.steps-timeline {
  position: relative;
  padding: 80px 0 60px; /* Increased top padding to accommodate numbers */
}

.timeline-line {
  position: absolute;
  top: 50%;
  left: 10%;
  right: 10%;
  height: 2px;
  background: linear-gradient(90deg, #667eea, #764ba2, #667eea);
  transform: translateY(-50%);
  opacity: 0.3;
}

.steps-container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  position: relative;
}

.step-item {
  position: relative;
  animation: fadeInUp 0.8s ease-out var(--delay) both;
}

.step-card {
  padding: 40px 20px 30px; /* Increased top padding */
  text-align: center;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: visible; /* Changed from hidden to visible */
}

.step-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.step-number {
  position: absolute;
  top: -20px; /* Keep the position */
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  z-index: 3; /* Increased z-index */
}

.step-icon-wrapper {
  margin-bottom: 20px;
  animation: float 6s infinite ease-in-out;
  animation-delay: calc(var(--delay) * 2);
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.step-content h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1f2937;
}

.theme--dark .step-content h3 {
  color: #f9fafb;
}

.step-content p {
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
}

.theme--dark .step-content p {
  color: #d1d5db;
}

.step-hover-effect {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
  transform: scale(0);
  transition: transform 0.5s ease;
  pointer-events: none;
  border-radius: 24px; /* Match the card's border radius */
}

.step-card:hover .step-hover-effect {
  transform: scale(1);
}

/* Interface Section */
.interface-section {
  margin: 100px 0;
  padding: 60px;
  position: relative;
  overflow: hidden;
}

.interface-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.sub-section-title {
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 16px;
}

.interface-subtitle {
  font-size: 24px;
  color: #667eea;
  margin-bottom: 20px;
  font-weight: 600;
}

.interface-desc {
  font-size: 18px;
  color: #6b7280;
  line-height: 1.6;
}

.theme--dark .interface-desc {
  color: #d1d5db;
}

/* Chat Preview */
.chat-preview {
  background: #f9fafb;
  border-radius: 16px;
  padding: 24px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.05);
}

.theme--dark .chat-preview {
  background: #1f2937;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);
}

.chat-bubble {
  padding: 12px 20px;
  border-radius: 20px;
  margin-bottom: 16px;
  font-size: 15px;
  animation: slideIn 0.5s ease-out both;
}

.chat-bubble.user {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  margin-left: auto;
  max-width: 80%;
  border-bottom-right-radius: 6px;
  animation-delay: 0.5s;
}

.chat-bubble.ai {
  background: white;
  color: #1f2937;
  max-width: 80%;
  border-bottom-left-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  animation-delay: 1s;
}

.theme--dark .chat-bubble.ai {
  background: #374151;
  color: #f9fafb;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateX(20px); }
  to { opacity: 1; transform: translateX(0); }
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 16px 20px;
  animation: slideIn 0.5s ease-out 1.5s both;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: #9ca3af;
  border-radius: 50%;
  animation: bounce 1s infinite;
}

.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

/* Use Cases Grid */
.use-cases {
  margin-bottom: 100px;
}

.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  padding: 0 20px;
}

.use-case-card {
  padding: 30px;
  text-align: center;
  position: relative;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: fadeInUp 0.8s ease-out var(--delay) both;
  overflow: hidden;
}

.use-case-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.use-case-icon {
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.use-case-card h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1f2937;
  position: relative;
  z-index: 1;
}

.theme--dark .use-case-card h4 {
  color: #f9fafb;
}

.use-case-card p {
  color: #6b7280;
  font-size: 14px;
  margin: 0;
  position: relative;
  z-index: 1;
}

.theme--dark .use-case-card p {
  color: #d1d5db;
}

.use-case-hover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at center, rgba(102, 126, 234, 0.08) 0%, transparent 70%);
  transform: scale(0);
  transition: transform 0.5s ease;
  pointer-events: none;
}

.use-case-card:hover .use-case-hover {
  transform: scale(1.5);
}

/* CTA Section */
.cta-section {
  margin: 100px 0;
  padding: 80px 60px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 2;
}

.cta-title {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 16px;
}

.cta-subtitle {
  font-size: 20px;
  color: #6b7280;
  margin-bottom: 40px;
}

.theme--dark .cta-subtitle {
  color: #d1d5db;
}

.cta-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}

.main-cta {
  padding: 18px 48px !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3) !important;
}

.cta-features {
  display: flex;
  gap: 30px;
  justify-content: center;
  flex-wrap: wrap;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 16px;
}

.theme--dark .feature-item {
  color: #d1d5db;
}

.cta-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.decoration-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.1;
}

.decoration-circle:nth-child(1) {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  top: -150px;
  right: -150px;
  animation: rotate 20s linear infinite;
}

.decoration-circle:nth-child(2) {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  bottom: -200px;
  left: -200px;
  animation: rotate 25s linear infinite reverse;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Quick Actions */
.quick-actions {
  position: fixed;
  bottom: 40px;
  right: 40px;
  z-index: 100;
  text-align: center;
}

.quick-title {
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 12px;
  opacity: 0.8;
}

.theme--dark .quick-title {
  color: #d1d5db;
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.action-fab {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  animation: fadeInUp 0.6s ease-out var(--delay) both, float 6s infinite ease-in-out var(--delay);
}

.action-fab:hover {
  transform: scale(1.1) translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
}

.action-labels {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #9ca3af;
}

.action-labels span {
  flex: 1;
  white-space: nowrap;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .steps-container {
    grid-template-columns: repeat(2, 1fr);
  }

  .timeline-line {
    display: none;
  }

  .interface-content {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .chat-preview {
    max-width: 500px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .welcome-content {
    padding: 20px;
  }

  .hero-section {
    padding: 40px 20px;
  }

  .hero-title {
    font-size: 36px;
  }

  .hero-subtitle {
    font-size: 18px;
  }

  .hero-cta {
    flex-direction: column;
    align-items: stretch;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .steps-container {
    grid-template-columns: 1fr;
  }

  .use-cases-grid {
    grid-template-columns: 1fr;
  }

  .cta-section {
    padding: 60px 30px;
  }

  .cta-title {
    font-size: 32px;
  }

  .cta-features {
    flex-direction: column;
    gap: 16px;
  }

  .quick-actions {
    bottom: 20px;
    right: 20px;
  }

  .action-buttons {
    gap: 8px;
  }

  .action-fab {
    width: 48px !important;
    height: 48px !important;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }

  .section-title {
    font-size: 24px;
  }

  .glass-card {
    border-radius: 16px;
    padding: 20px;
  }

  .action-labels {
    display: none;
  }
}

/* Print Styles */
@media print {
  .quick-actions {
    display: none;
  }

  .animated-bg {
    display: none;
  }

  .glass-card {
    background: white;
    box-shadow: none;
  }
}

/* Animations and Transitions */
.welcome-container * {
  transition: color 0.3s ease, background-color 0.3s ease, border-color 0.3s ease;
}

/* Loading State */
.loading-state {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.5s ease;
}

.loading-state.active {
  opacity: 1;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Smooth Scroll */
html {
  scroll-behavior: smooth;
}

/* Focus States */
.v-btn:focus::before {
  opacity: 0.1 !important;
}

.glass-button:focus {
  outline: 2px solid #667eea;
  outline-offset: 2px;
}

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .glass-card {
    border: 2px solid currentColor;
  }

  .gradient-text {
    -webkit-text-fill-color: currentColor;
    color: currentColor;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .welcome-container * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 4px;
  transition: background 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.theme--dark ::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.4);
}

.theme--dark ::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.6);
}

/* Performance Optimizations */
.welcome-container {
  will-change: scroll-position;
}

.animated-bg::before,
.animated-bg::after,
.decoration-circle {
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
}

.glass-card {
  transform: translateZ(0);
  will-change: transform;
}

/* Easter Egg Animation */
.hero-title:hover .gradient-text {
  animation: rainbow 3s linear infinite;
}

@keyframes rainbow {
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
}
</style>
